/**
 * Fuzzy string matching used by the command palette.
 *
 * `fuzzyScore` returns a relevance score in `[0, 1]` (higher = better) for a
 * single query token against a single target, or `null` when the query does
 * not match at all. Matching is case- and accent-insensitive and ranks, in
 * order of quality: exact match, substring match, word-prefix match, then
 * loose subsequence (characters in order, gaps allowed) — so `"tsla"` finds
 * "Tesla Motors".
 *
 * `commandFilter` adapts the scorer to cmdk's `filter` contract
 * `(value, search, keywords) => 0..1`, where 0 hides the item. It splits the
 * query into words and requires *every* word to match the item's value or one
 * of its keywords, so a natural phrase like "go to home" still surfaces the
 * "Home" navigation command through its breadcrumb keywords — a contiguous
 * single-string matcher could never line a multi-word phrase up with a
 * single-word command title.
 */

const DIACRITICS_RE = /[\u0300-\u036f]/g;

/** Lowercase, accent-stripped form used for matching. */
export function normalize(input: string): string {
  return input.normalize("NFKD").replace(DIACRITICS_RE, "").toLowerCase();
}

/** Split a query into individual search tokens (normalized). */
const tokenize = (query: string): string[] =>
  normalize(query).split(/\s+/).filter(Boolean);

/**
 * Score a query against a single target string.
 * Returns `null` when there is no match, otherwise a score in `[0, 1]`.
 */
export function fuzzyScore(query: string, target: string): number | null {
  const q = normalize(query);
  const t = normalize(target);
  if (!q) return 0;
  if (t === q) return 1;

  const at = t.indexOf(q);
  if (at !== -1) {
    // Substring — earlier occurrences rank higher. This also covers word
    // prefixes ("app" → "Apple", "sys" → "design-system showcase").
    return Math.max(0.5, 0.85 - at * 0.01);
  }

  return subsequenceScore(q, t);
}

/**
 * Characters must appear in order, gaps are penalized. Returns `null` when the
 * query is not a subsequence of the target.
 */
function subsequenceScore(q: string, t: string): number | null {
  let qi = 0;
  let prev = -1;
  let gaps = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      if (prev !== -1) gaps += Math.max(0, ti - prev - 1);
      prev = ti;
      qi++;
    }
  }
  if (qi < q.length) return null;

  const density = q.length / t.length;
  const gapPenalty = 1 - Math.min(gaps / Math.max(t.length, 1), 0.6);
  return Math.min(0.6, density * 0.35 + gapPenalty * 0.4);
}

/**
 * Match a single token against an item, falling back to its keywords.
 * Keyword hits are discounted below direct value hits so a match on the
 * command's own name always outranks a coincidental keyword hit
 * ("app" → "Apps", never "appearance").
 */
function tokenScore(
  token: string,
  value: string,
  keywords: string[],
): number | null {
  const direct = fuzzyScore(token, value);
  if (direct !== null) return direct;
  let best = 0;
  for (const keyword of keywords) {
    const score = fuzzyScore(token, keyword);
    if (score !== null) best = Math.max(best, score * 0.9);
  }
  return best > 0 ? best : null;
}

/** cmdk `filter` adapter: every query token must match the value or a keyword. */
export function commandFilter(
  value: string,
  search: string,
  keywords?: string[],
): number {
  const tokens = tokenize(search);
  if (tokens.length === 0) return 0;
  const kws = keywords ?? [];
  let total = 0;
  for (const token of tokens) {
    const score = tokenScore(token, value, kws);
    if (score === null) return 0;
    total += score;
  }
  // Mean token score: a phrase that matches every word ranks above one that
  // only matches on a coincidence.
  return total / tokens.length;
}
